// Statement-completion change emission for an expo-sqlite-surface connection.
//
// Wraps a connection's query methods IN PLACE (mirroring
// `createPerformanceLogger`'s technique) so that every MUTATING statement emits
// a manual change event at completion — after the write finished, before the
// caller's promise resolves. This is the single, platform- and engine-uniform
// producer of the deterministic table-level feed (see
// `buffered-manual-change-feed.ts`): the native update hook delivers its events
// a bridge-hop later, which is exactly the window that let a post-write read
// observe a not-yet-invalidated cache.
//
// Mutations are detected from the SQL, not the method: drizzle runs
// `INSERT … RETURNING` through `getAllAsync`, so every query method is
// sniffed. `begin`/`commit`/savepoint statements don't match the mutating-verb
// regex, so transaction framing emits nothing; per-statement events inside a
// transaction are buffered and flushed post-COMMIT by the transaction buffer.

import { emitManualDatabaseChange } from "./manual-change-listener.ts";
import { extractMutatedTable, splitSqlStatements } from "./extract-mutated-table.ts";

const CHANGE_FEED_ATTACHED = Symbol.for("han.sqliteChangeFeedAttached");

type UnknownAsyncMethod = (...args: unknown[]) => Promise<unknown>;

function isUnknownFunction(value: unknown): value is UnknownAsyncMethod {
  return typeof value === "function";
}

function getSqlArgument(args: readonly unknown[]): string | null {
  const [sql] = args;
  return typeof sql === "string" ? sql : null;
}

function readLastInsertRowId(result: unknown): number {
  if (typeof result !== "object" || result === null) {
    return 0;
  }
  const rowId = Reflect.get(result, "lastInsertRowId") ?? Reflect.get(result, "lastInsertRowid");
  return typeof rowId === "number" ? rowId : 0;
}

function emitForStatement(databaseFilePath: string, sql: string, lastInsertRowId: number): void {
  const tableName = extractMutatedTable(sql);
  if (tableName === null) {
    return;
  }
  emitManualDatabaseChange({
    databaseFilePath,
    databaseName: "main",
    rowId: lastInsertRowId,
    tableName,
  });
}

function wrapQueryMethod(db: object, method: string, databaseFilePath: string): void {
  const originalMethod = Reflect.get(db, method);
  if (!isUnknownFunction(originalMethod)) {
    return;
  }
  Reflect.set(db, method, async (...args: unknown[]) => {
    const result = await Reflect.apply(originalMethod, db, args);
    const sql = getSqlArgument(args);
    if (sql !== null) {
      if (method === "execAsync") {
        for (const statement of splitSqlStatements(sql)) {
          emitForStatement(databaseFilePath, statement, 0);
        }
      } else {
        emitForStatement(databaseFilePath, sql, readLastInsertRowId(result));
      }
    }
    return result;
  });
}

function wrapPrepareAsync(db: object, databaseFilePath: string): void {
  const originalMethod = Reflect.get(db, "prepareAsync");
  if (!isUnknownFunction(originalMethod)) {
    return;
  }
  Reflect.set(db, "prepareAsync", async (...args: unknown[]) => {
    const statement = await Reflect.apply(originalMethod, db, args);
    const sql = getSqlArgument(args);
    if (sql === null || typeof statement !== "object" || statement === null) {
      return statement;
    }
    // Native/adapter statements expose `executeAsync`; the web driver's expose
    // `executeForRawResultAsync` (drizzle's `.values()` path). Wrap whichever
    // exist — `INSERT … RETURNING` flows through these too.
    for (const method of ["executeAsync", "executeForRawResultAsync"]) {
      const originalExecute = Reflect.get(statement, method);
      if (!isUnknownFunction(originalExecute)) {
        continue;
      }
      Reflect.set(statement, method, async (...executeArgs: unknown[]) => {
        const result = await Reflect.apply(originalExecute, statement, executeArgs);
        emitForStatement(databaseFilePath, sql, readLastInsertRowId(result));
        return result;
      });
    }
    return statement;
  });
}

/**
 * Attach statement-completion change emission to `db` (idempotent). Call on
 * every connection whose writes must drive the deterministic table-level feed
 * — currently the actor connection, in both its engines (local expo-sqlite and
 * turso-sync adapter) and on web.
 */
export function attachSqliteChangeFeed<Database extends object>(
  db: Database,
  databaseFilePath: string,
): Database {
  if (Reflect.get(db, CHANGE_FEED_ATTACHED) === true) {
    return db;
  }
  Reflect.set(db, CHANGE_FEED_ATTACHED, true);

  const withScope: unknown = Reflect.get(db, "withTransactionScope");
  if (typeof withScope === "function") {
    Reflect.set(db, "withTransactionScope", (body: (client: object) => Promise<unknown>) =>
      Reflect.apply(withScope, db, [
        (client: object) => body(attachSqliteChangeFeed(client, databaseFilePath)),
      ]),
    );
  }
  for (const method of ["runAsync", "getAllAsync", "getFirstAsync", "execAsync"]) {
    wrapQueryMethod(db, method, databaseFilePath);
  }
  wrapPrepareAsync(db, databaseFilePath);
  return db;
}
